import { Injectable } from '@angular/core';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Teaches NgbDatepicker to read and write mm/dd/yyyy.
 *
 * This replaces the old maskDate()/normalizeDate() pair on the component: the
 * datepicker calls parse() whenever the user types into the box and format()
 * whenever it writes a value back, so the same flexible-in / canonical-out
 * behaviour now lives in one place and applies to the popup as well.
 *
 * Accepted on input: 12/31/2099, 1/5/2027, 1/5/27, 12/31 (current year),
 * and a bare 12312099. Rejected: 02/30/2027, month 13, 3- or 5-digit years.
 * Always written back as MM/dd/yyyy.
 *
 * Provided at component level (not root) so it only affects this component.
 */
@Injectable()
export class datePickerDateParserFormatter extends NgbDateParserFormatter {

  parse(value: string): NgbDateStruct | null {
    const raw = (value ?? '').trim();
    if (!raw) { return null; }

    let parts: string[];
    if (/^\d{8}$/.test(raw)) {
      // Pasted or typed without separators: 12312099
      parts = [raw.slice(0, 2), raw.slice(2, 4), raw.slice(4)];
    } else {
      parts = raw.split('/').filter(p => p !== '');
    }
    if (parts.length < 2 || parts.length > 3) { return null; }
    if (!parts.every(p => /^\d+$/.test(p))) { return null; }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    let year: number;
    if (parts.length === 2) {
      // No year typed ("12/31" or "12/31/") — assume the current one
      year = new Date().getFullYear();
    } else if (parts[2].length === 2) {
      year = 2000 + Number(parts[2]);   // "27" -> 2027, "99" -> 2099
    } else if (parts[2].length === 4) {
      year = Number(parts[2]);
    } else {
      // A one-, three- or five-digit year is a typo, not a shorthand
      return null;
    }

    if (month < 1 || month > 12 || day < 1) { return null; }
    // Round-trip through Date to reject overflowing days like 02/30,
    // which Date would otherwise roll forward into March
    const probe = new Date(year, month - 1, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
      return null;
    }
    return { year, month, day };
  }

  format(date: NgbDateStruct | null): string {
    if (!date) { return ''; }
    return `${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')}/${String(date.year).padStart(4, '0')}`;
  }
}
